import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AppText, Languages, UserProfileAction } from 'src/app/interfaces/common';
import { DisplayService } from 'src/app/services/display.service';
import { UserService } from 'src/app/services/user.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-edit-user-profile',
  templateUrl: './edit-user-profile.component.html',
  styleUrls: ['./edit-user-profile.component.css']
})
export class EditUserProfileComponent implements OnInit {
  @Output() closeEditUserProfilee = new EventEmitter();
  @Input() selText: AppText = {};
  @Input() applicationSettings: any;
  @Input() lang: Languages = Languages.Icelandic;
  @Input() editAction: string = "";

  currentPasswordFieldType: string = 'password';
  newPasswordFieldType: string = 'password';
  changePasswordForm!: FormGroup;
  userProfileAction = UserProfileAction;

  constructor(
    private fb: FormBuilder,
    private http: HttpClient,
    private userService: UserService,
    private displayService: DisplayService
  ) { }

  ngOnInit(): void {
    this.initForm();
  }

  initForm(): void {
    this.changePasswordForm = this.fb.group({
      currentPassword: ['', [(control: AbstractControl) => Validators.required(control)]],
      newPassword: ['', [
        (control: AbstractControl) => Validators.required(control),
        (control: AbstractControl) => Validators.minLength(8)(control)
      ]],
      confirmPassword: ['', [(control: AbstractControl) => Validators.required(control)]]
    });
  }

  closeEditUserProfile(): void {
    this.closeEditUserProfilee.emit("");
  }

  togglePasswordVisibility(event: PointerEvent, field: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (field === 'current') {
      this.currentPasswordFieldType = this.currentPasswordFieldType === 'password' ? 'text' : 'password';
    } else if (field === 'new') {
      this.newPasswordFieldType = this.newPasswordFieldType === 'password' ? 'text' : 'password';
    }
  }

  onChangePasswordSubmit(): void {
    if (this.changePasswordForm.invalid) {
      this.displayService.toastrShowError({ message: this.selText.invalidForm });
      return;
    }

    const formValue = {
      currentPassword: (this.changePasswordForm.value.currentPassword ?? '').trim(),
      newPassword: (this.changePasswordForm.value.newPassword ?? '').trim(),
      confirmPassword: (this.changePasswordForm.value.confirmPassword ?? '').trim()
    };

    // Validate non-empty fields after trimming
    if (!formValue.currentPassword || !formValue.newPassword || !formValue.confirmPassword) {
      this.displayService.toastrShowError({ message: this.selText.emptyPasswordField });
      return;
    }

    // Validate new password length after trimming
    if (formValue.newPassword.length < 8) {
      this.displayService.toastrShowError({ message: this.selText.passwordTooShort });
      return;
    }

    // Validate matching passwords after trimming
    if (formValue.newPassword !== formValue.confirmPassword) {
      this.displayService.toastrShowError({ message: this.selText.mismatchingPasswords });
      return;
    }

    const userData = this.userService.getUserData();
    if (!userData?.User?.gid) {
      this.displayService.toastrShowError({ message: this.selText.missingUserIdentifier });
      return;
    }

    const payload = {
      action: "new-password",
      oldPassword: formValue.currentPassword,
      newPassword: formValue.newPassword,
      user_gid: userData.User.gid,
      return_url: environment.loginURL
    };

    this.http.post<any>(environment.alterUserProfileURL, payload).subscribe({
      next: (response) => {
        const error = response?.error?.toLowerCase?.() ?? "";

        if (error === "wrong data") {
          this.displayService.toastrShowError({ message: this.selText.currentPasswordIncorrect });
          return;
        }
        else if (error) {
          this.displayService.toastrShowError({ message: this.selText.passwordSavedUnsuccessfully });
          return;
        }
        else {
          this.displayService.toastrShowSuccess({ message: this.selText.passwordSavedSuccessfully });
        }
      },
      error: () => {
        this.displayService.toastrShowError({ message: this.selText.passwordSavedUnsuccessfully });
      },
      complete: () => {
        this.closeEditUserProfile();
      }
    });
  }
}